import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  MAX_CLIP_SECONDS,
  MAX_CLIP_UPLOAD_BYTES,
  clipKindLabel,
  clipLimitReached,
  clipNeedsLocation,
  clipRefusal,
  formatClipTime,
  myClipStateLabel,
  type ClientMessage,
  type ClipKind,
  type Coords,
  type MyClipsState,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { bestLocation } from '../lib/locate.ts';
import { timeShort } from '../lib/format.ts';

/**
 * Botschaft an die Regie: eine kurze Video- oder Sprachnachricht aufnehmen
 * (oder aus der Galerie nehmen) und hochladen. Die Regie entscheidet danach,
 * ob und wann sie im Stream laeuft; den Stand sieht der Absender darunter.
 *
 * Aufgenommen wird im Browser mit `MediaRecorder` -- iOS liefert MP4, Chrome
 * meist WebM. Was herauskommt, ist egal: der Server wandelt alles um.
 *
 * Auf der Senderseite gibt es schon eine Kamera (`source`). Ein zweites
 * `getUserMedia` wuerde sie auf manchen Handys dem Livestream wegnehmen;
 * deshalb nimmt der Recorder dort *Kopien* ihrer Spuren (`clone`). Kopien
 * haben ihren eigenen An/Aus-Schalter -- hat die Regie das Mikro dieser
 * Kamera fuer den Stream abgeschaltet, ist es in der Botschaft trotzdem da.
 *
 * Eine Kopie hat aber die Aufloesung ihrer Quelle, und die Live-Kamera laeuft
 * in 720p. Hoeher stellen laesst sich nur die Quelle selbst -- das macht die
 * Seite ueber `onSourceHd`, solange aufgenommen wird; ihr Livebild geht dabei
 * weiter mit 720p raus (`Publisher.limitVideo`).
 */

type Step = 'choose' | 'record' | 'review' | 'upload' | 'done';

interface Take {
  blob: Blob;
  url: string;
  kind: ClipKind;
  durationMs: number | null;
}

/** Was dieser Browser aufnehmen kann -- in dieser Reihenfolge versucht. */
const VIDEO_TYPES = ['video/mp4;codecs=avc1,mp4a', 'video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];
const AUDIO_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

function recorderType(kind: ClipKind): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const list = kind === 'video' ? VIDEO_TYPES : AUDIO_TYPES;
  return list.find((t) => MediaRecorder.isTypeSupported?.(t));
}

const canRecord = () => typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/** Laenge einer gewaehlten Datei -- null, wenn der Browser sie nicht kennt (WebM ohne Kopf). */
function measure(url: string, kind: ClipKind): Promise<number | null> {
  return new Promise((resolve) => {
    const el = document.createElement(kind === 'video' ? 'video' : 'audio');
    el.preload = 'metadata';
    const done = (value: number | null) => {
      el.removeAttribute('src');
      el.load();
      resolve(value);
    };
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration * 1000 : null);
    el.onerror = () => done(null);
    el.src = url;
  });
}

const MEDIA_ERRORS: Record<string, string> = {
  NotAllowedError: 'Kamera oder Mikrofon sind nicht erlaubt — bitte in den Einstellungen des Browsers freigeben.',
  NotFoundError: 'Keine Kamera bzw. kein Mikrofon gefunden.',
  NotReadableError: 'Kamera oder Mikrofon sind gerade von einer anderen App belegt.',
};

export function ClipRecorder({
  slug,
  connectionId,
  mine,
  send,
  source,
  onSourceHd,
  highlight = false,
  onActive,
  open,
  onOpenChange,
}: {
  slug: string;
  /** Aus dem eigenen `welcome` -- daran erkennt der Server die Sitzung. */
  connectionId: string | null;
  mine: MyClipsState;
  send: (msg: ClientMessage) => void;
  /** Eine schon offene Kamera (Senderseite) -- dann wird von ihr aufgenommen. */
  source?: MediaStream | null;
  /** Diese Kamera fuer die Aufnahme auf 1080p stellen (`true`) und danach zurueck. */
  onSourceHd?: (on: boolean) => Promise<void>;
  /** Hervorheben, etwa solange die Freigabe auf sich warten laesst. */
  highlight?: boolean;
  /** Das Fenster ist offen -- die Zuschauerseite schaltet so lange den Stream stumm. */
  onActive?: (active: boolean) => void;
  /**
   * Von aussen geoeffnet, etwa ueber den Knopf am Video der Zuschauerseite.
   * Ohne diese beiden oeffnet nur der Knopf hier im Kasten.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const isOpen = open ?? ownOpen;
  const refusal = clipRefusal(mine);

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setOwnOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );
  const close = useCallback(() => setOpen(false), [setOpen]);
  useEffect(() => {
    onActive?.(isOpen);
  }, [isOpen, onActive]);

  return (
    <div className={`clip-box${highlight ? ' is-highlight' : ''}`}>
      <div className="clip-box__head">
        <b>Botschaft an die Regie</b>
        {mine.allowed !== null && (
          <span className="clip-box__quota">
            {Math.min(mine.sent, mine.allowed)} von {mine.allowed}
          </span>
        )}
      </div>
      <p className="clip-box__text">
        {highlight
          ? 'Die Freigabe dauert? Nimm eine Video- oder Sprachnachricht auf — die Regie kann sie später im Stream zeigen.'
          : `Video oder Sprachnachricht, bis ${MAX_CLIP_SECONDS} Sekunden. Die Regie entscheidet, ob und wann sie im Stream läuft.`}
      </p>
      {refusal ? (
        <div className="clip-box__refusal">
          <span>{refusal}</span>
          {clipLimitReached(mine) && !mine.blocked && !mine.wantsMore && (
            <button type="button" className="btn btn--sm" onClick={() => send({ t: 'clip:ask' })}>
              Um weitere bitten
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--block clip-box__go"
          disabled={!connectionId}
          onClick={() => setOpen(true)}
        >
          🎬 Botschaft aufnehmen
        </button>
      )}

      {mine.clips.length > 0 && (
        <ul className="clip-mine">
          {mine.clips.map((clip) => (
            <li key={clip.id} className={`clip-mine__row is-${clip.state}`}>
              <span aria-hidden>{clip.kind === 'video' ? '🎥' : '🎤'}</span>
              <span className="clip-mine__what">
                {clipKindLabel[clip.kind]}
                {clip.durationMs > 0 ? ` · ${formatClipTime(clip.durationMs)}` : ''}
                <em>{timeShort.format(new Date(clip.createdAt))}</em>
              </span>
              <span className="clip-mine__state" title={clip.error}>
                {myClipStateLabel[clip.state]}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Ueber die ganze Seite -- der Kasten kann am Handy in einem versteckten Reiter liegen. */}
      {isOpen &&
        connectionId &&
        createPortal(
          <ClipSheet
            slug={slug}
            connectionId={connectionId}
            source={source ?? null}
            onSourceHd={onSourceHd}
            refusal={refusal}
            needsLocation={clipNeedsLocation(mine)}
            canAsk={clipLimitReached(mine) && !mine.blocked && !mine.wantsMore}
            onAsk={() => send({ t: 'clip:ask' })}
            onClose={close}
          />,
          document.body,
        )}
    </div>
  );
}

/* ------------------------------------------------------------- Fenster */

function ClipSheet({
  slug,
  connectionId,
  source,
  onSourceHd,
  refusal,
  needsLocation,
  canAsk,
  onAsk,
  onClose,
}: {
  slug: string;
  connectionId: string;
  source: MediaStream | null;
  onSourceHd?: (on: boolean) => Promise<void>;
  /** Darf gerade nicht schicken -- dann steht der Grund da statt der Auswahl. */
  refusal: string | null;
  /** „Nur vor Ort": erst den Standort pruefen, dann aufnehmen. */
  needsLocation: boolean;
  canAsk: boolean;
  onAsk: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>('choose');
  const [kind, setKind] = useState<ClipKind>('video');
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<Take | null>(null);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<HTMLVideoElement | null>(null);
  /*
   * „Nur vor Ort": bevor jemand aufnimmt, fragt der Server, ob der Standort
   * im Event-Bereich liegt. Ein Nein kommt so vor der Aufnahme und nicht
   * nach 90 Sekunden. Mitgeschickt wird der Standort auch beim Upload --
   * der Server prueft dort noch einmal.
   */
  const [gate, setGate] = useState<'checking' | 'ok' | 'refused'>(needsLocation ? 'checking' : 'ok');
  const [gateError, setGateError] = useState<string | null>(null);
  const [location, setLocation] = useState<Coords | null>(null);
  const checkPlace = useCallback(async () => {
    setGate('checking');
    setGateError(null);
    const found = await bestLocation();
    setLocation(found.location);
    try {
      await api.checkClip(slug, connectionId, found.location);
      setGate('ok');
    } catch (err) {
      // Die Antwort des Servers sagt auch, *warum* der Standort zaehlt -- „Standort
      // nicht freigegeben" allein liesse offen, wozu er ueberhaupt gebraucht wird.
      setGateError((err as Error).message);
      setGate('refused');
    }
  }, [slug, connectionId]);
  useEffect(() => {
    if (needsLocation) void checkPlace();
  }, [needsLocation, checkPlace]);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const startedRef = useRef(0);
  const fileRef = useRef<HTMLInputElement | null>(null);

  /** Eigene Spuren bzw. Kopien wieder freigeben -- die Kamera der Seite bleibt an. */
  const release = useCallback((s: MediaStream | null) => s?.getTracks().forEach((t) => t.stop()), []);

  /*
   * Die Kamera der Seite steht auf 1080p, solange hier aufgenommen wird --
   * und nur so lange. Zurueck geht es, sobald der Schritt „Aufnahme" vorbei
   * ist, auf welchem Weg auch immer: fertig, zurueck, Fenster zu.
   */
  const hdRef = useRef(false);
  const hdOffRef = useRef(onSourceHd);
  hdOffRef.current = onSourceHd;
  const leaveHd = useCallback(() => {
    if (!hdRef.current) return;
    hdRef.current = false;
    void hdOffRef.current?.(false);
  }, []);
  useEffect(() => {
    if (step !== 'record') leaveHd();
  }, [step, leaveHd]);
  useEffect(() => leaveHd, [leaveHd]);

  // Beim Schliessen: Aufnahme beenden, Spuren und Vorschau freigeben.
  const streamRef = useRef<MediaStream | null>(null);
  streamRef.current = stream;
  const takeRef = useRef<Take | null>(null);
  takeRef.current = take;
  useEffect(
    () => () => {
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      release(streamRef.current);
      if (takeRef.current) URL.revokeObjectURL(takeRef.current.url);
    },
    [release],
  );

  useEffect(() => {
    if (previewRef.current) previewRef.current.srcObject = stream;
  }, [stream, step]);

  /** Kamera bzw. Mikrofon fuer die Aufnahme oeffnen. */
  const open = useCallback(
    async (nextKind: ClipKind, nextFacing: 'user' | 'environment') => {
      setError(null);
      release(streamRef.current);
      setStream(null);
      try {
        let s: MediaStream;
        const fromPage = source?.getTracks().filter((t) => t.readyState === 'live' && (nextKind === 'video' || t.kind === 'audio'));
        if (fromPage?.length && (nextKind === 'audio' || fromPage.some((t) => t.kind === 'video'))) {
          /*
           * Erst die Quelle auf 1080p, dann kopieren: eine Kopie hat die
           * Aufloesung ihrer Quelle und laesst sich selbst nicht hoeher stellen
           * (der Browser antwortet mit OverconstrainedError).
           */
          if (nextKind === 'video' && onSourceHd) {
            hdRef.current = true;
            await onSourceHd(true);
          }
          s = new MediaStream(
            fromPage.map((t) => {
              const copy = t.clone();
              copy.enabled = true;
              return copy;
            }),
          );
        } else {
          s = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
            video:
              nextKind === 'video'
                ? { facingMode: nextFacing, width: { ideal: 1920 }, height: { ideal: 1080 } }
                : false,
          });
        }
        setStream(s);
        setKind(nextKind);
        setStep('record');
      } catch (err) {
        const name = (err as { name?: string })?.name ?? '';
        setError(MEDIA_ERRORS[name] ?? 'Kamera bzw. Mikrofon ließen sich nicht öffnen.');
      }
    },
    [release, source, onSourceHd],
  );

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') recorder.stop();
  }, []);

  const startRecording = () => {
    if (!stream) return;
    const type = recorderType(kind);
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(type ? { mimeType: type } : {}),
        // 1080p mit Luft fuer die zweite Umwandlung auf dem Server -- 90 s sind so gut 55 MB.
        videoBitsPerSecond: 5_000_000,
        audioBitsPerSecond: 96_000,
      });
    } catch {
      setError('Dieser Browser kann hier nicht aufnehmen — wähle stattdessen ein Video aus der Galerie.');
      return;
    }
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    recorder.onstop = () => {
      setRecording(false);
      const durationMs = Math.min(Date.now() - startedRef.current, MAX_CLIP_SECONDS * 1000);
      const blob = new Blob(chunks, { type: (recorder.mimeType || type || '').split(';')[0] || 'video/mp4' });
      if (blob.size === 0) {
        setError('Die Aufnahme ist leer geblieben — bitte noch einmal.');
        return;
      }
      release(streamRef.current);
      setStream(null);
      setTake({ blob, url: URL.createObjectURL(blob), kind, durationMs });
      setStep('review');
    };
    recorderRef.current = recorder;
    startedRef.current = Date.now();
    setElapsed(0);
    recorder.start(1000);
    setRecording(true);
  };

  // Laufzeit zaehlen und bei 90 Sekunden von selbst anhalten.
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => {
      const ms = Date.now() - startedRef.current;
      setElapsed(ms);
      if (ms >= MAX_CLIP_SECONDS * 1000) stopRecording();
    }, 250);
    return () => window.clearInterval(timer);
  }, [recording, stopRecording]);

  const pickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (file.size > MAX_CLIP_UPLOAD_BYTES) {
      setError(`Die Datei ist zu groß (höchstens ${MAX_CLIP_UPLOAD_BYTES / 1024 / 1024} MB).`);
      return;
    }
    const fileKind: ClipKind = file.type.startsWith('audio/') ? 'audio' : 'video';
    const url = URL.createObjectURL(file);
    const durationMs = await measure(url, fileKind);
    if (durationMs !== null && durationMs > MAX_CLIP_SECONDS * 1000 + 500) {
      URL.revokeObjectURL(url);
      setError(`Das ist ${formatClipTime(durationMs)} lang — eine Botschaft darf höchstens ${MAX_CLIP_SECONDS} Sekunden haben.`);
      return;
    }
    release(streamRef.current);
    setStream(null);
    setTake({ blob: file, url, kind: fileKind, durationMs });
    setStep('review');
  };

  const again = () => {
    if (take) URL.revokeObjectURL(take.url);
    setTake(null);
    setConsent(false);
    setStep('choose');
  };

  const upload = async () => {
    if (!take || !consent) return;
    setStep('upload');
    setProgress(0);
    setError(null);
    try {
      await api.uploadClip(slug, connectionId, take.blob, take.durationMs, needsLocation ? location : null, setProgress);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hochladen fehlgeschlagen.');
      setStep('review');
    }
  };

  const recordable = canRecord();

  return (
    <div className="clip-sheet" role="dialog" aria-modal="true" aria-label="Botschaft an die Regie">
      <div className="clip-sheet__card">
        <div className="clip-sheet__head">
          <b>
            {step === 'record' ? clipKindLabel[kind] : step === 'done' ? 'Angekommen' : 'Botschaft an die Regie'}
          </b>
          <button type="button" className="clip-sheet__close" onClick={onClose} aria-label="Schließen" disabled={step === 'upload'}>
            ✕
          </button>
        </div>

        {error && <div className="notice notice--stage">{error}</div>}

        {step === 'choose' && refusal && (
          <div className="clip-box__refusal">
            <span>{refusal}</span>
            {canAsk && (
              <button type="button" className="btn btn--sm" onClick={onAsk}>
                Um weitere bitten
              </button>
            )}
          </div>
        )}

        {step === 'choose' && !refusal && gate === 'checking' && (
          <div className="clip-sheet__gate">
            <span className="clip-sheet__spinner" aria-hidden />
            <span>
              Standort wird geprüft … Botschaften nimmt die Regie gerade nur von Zuschauer:innen vor Ort an.
            </span>
          </div>
        )}
        {step === 'choose' && !refusal && gate === 'refused' && (
          <div className="clip-box__refusal">
            <span>{gateError}</span>
            <button type="button" className="btn btn--sm" onClick={() => void checkPlace()}>
              Erneut prüfen
            </button>
          </div>
        )}

        {step === 'choose' && !refusal && gate === 'ok' && (
          <div className="clip-sheet__choose">
            {recordable && (
              <>
                <button type="button" className="clip-choice" onClick={() => void open('video', facing)}>
                  <span aria-hidden>🎥</span>
                  <b>Video aufnehmen</b>
                  <small>bis {MAX_CLIP_SECONDS} Sekunden</small>
                </button>
                <button type="button" className="clip-choice" onClick={() => void open('audio', facing)}>
                  <span aria-hidden>🎤</span>
                  <b>Sprachnachricht</b>
                  <small>nur Ton</small>
                </button>
              </>
            )}
            <button type="button" className="clip-choice" onClick={() => fileRef.current?.click()}>
              <span aria-hidden>📁</span>
              <b>Aus der Galerie</b>
              <small>Video oder Ton</small>
            </button>
            <input ref={fileRef} type="file" accept="video/*,audio/*" hidden onChange={(e) => void pickFile(e)} />
          </div>
        )}
        {step === 'choose' && !refusal && gate === 'ok' && source && (
          <p className="clip-sheet__meta">
            Aufgenommen wird mit deiner Live-Kamera in Full HD. Wenn du gerade live bist, sieht man dich dabei
            weiter im Stream.
          </p>
        )}

        {step === 'record' && (
          <div className="clip-sheet__record">
            {kind === 'video' ? (
              <div className="clip-sheet__view">
                <video
                  ref={previewRef}
                  autoPlay
                  playsInline
                  muted
                  className={facing === 'user' && !source ? 'is-mirrored' : undefined}
                />
                {!source && !recording && (
                  <button
                    type="button"
                    className="clip-sheet__flip"
                    aria-label="Kamera wechseln"
                    title="Vordere oder hintere Kamera"
                    onClick={() => {
                      const next = facing === 'user' ? 'environment' : 'user';
                      setFacing(next);
                      void open('video', next);
                    }}
                  >
                    ⇄
                  </button>
                )}
              </div>
            ) : (
              <div className={`clip-sheet__mic${recording ? ' is-on' : ''}`} aria-hidden>
                🎤
              </div>
            )}
            <div className="clip-sheet__timer" role="timer">
              <span className={recording ? 'is-rec' : undefined}>{recording ? '● ' : ''}{formatClipTime(elapsed)}</span>
              <span> / {formatClipTime(MAX_CLIP_SECONDS * 1000)}</span>
            </div>
            <div className="clip-sheet__bar">
              <span style={{ width: `${Math.min(100, (elapsed / (MAX_CLIP_SECONDS * 1000)) * 100)}%` }} />
            </div>
            <div className="row gap-8">
              {recording ? (
                <button type="button" className="btn btn--block clip-sheet__stop" onClick={stopRecording}>
                  ■ Aufnahme beenden
                </button>
              ) : (
                <>
                  <button type="button" className="btn btn--ghost clip-sheet__back" onClick={again}>
                    Zurück
                  </button>
                  <button type="button" className="btn btn--block clip-sheet__rec" onClick={startRecording} disabled={!stream}>
                    ● Aufnahme starten
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {(step === 'review' || step === 'upload') && take && (
          <div className="clip-sheet__review">
            {take.kind === 'video' ? (
              <video src={take.url} controls playsInline className="clip-sheet__playback" />
            ) : (
              <audio src={take.url} controls className="clip-sheet__audio" />
            )}
            <div className="clip-sheet__meta">
              {clipKindLabel[take.kind]}
              {take.durationMs ? ` · ${formatClipTime(take.durationMs)}` : ''}
            </div>
            <label className="clip-sheet__consent">
              <input
                type="checkbox"
                checked={consent}
                disabled={step === 'upload'}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                Die Regie darf diese Botschaft im Livestream zeigen. Ich habe die Rechte daran, und wer darin zu
                sehen oder zu hören ist, ist einverstanden.
              </span>
            </label>
            {step === 'upload' ? (
              <div className="clip-sheet__upload">
                <div className="clip-sheet__bar">
                  <span style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
                <span>{progress < 1 ? `wird hochgeladen … ${Math.round(progress * 100)} %` : 'angekommen, wird aufbereitet …'}</span>
              </div>
            ) : (
              <div className="row gap-8">
                <button type="button" className="btn btn--ghost clip-sheet__back" onClick={again}>
                  Nochmal
                </button>
                <button type="button" className="btn btn--block" onClick={() => void upload()} disabled={!consent}>
                  An die Regie senden
                </button>
              </div>
            )}
          </div>
        )}

        {step === 'done' && (
          <div className="clip-sheet__done">
            <p>Die Regie sieht deine Botschaft jetzt. Ob sie freigegeben ist oder gerade im Stream läuft, steht unter dem Knopf.</p>
            <button type="button" className="btn btn--block" onClick={onClose}>
              Schließen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
